import React, { useEffect } from "react";

/**
 * ActionModal
 * A clean, accessible modal dialog that replaces browser alert() and window.confirm()
 * Supports confirmation prompts, alert/status notifications, and live progress indicators.
 */
export default function ActionModal({
  open,
  isOpen,
  type = "confirm", // "confirm" | "success" | "error" | "info" | "progress"
  title,
  message,
  detail = null,
  confirmLabel,
  confirmText,
  cancelLabel,
  cancelText,
  confirmTone = "primary", // "primary" | "danger" | "success"
  progress = 0, // 0 to 100
  progressLabel = "",
  busy = false,
  locked = false,
  isLocked,
  onConfirm,
  onCancel
}) {
  const modalOpen = Boolean(open !== undefined ? open : isOpen);
  const modalLocked = Boolean(locked !== undefined ? locked : isLocked);
  const finalConfirmLabel = confirmText || confirmLabel || (type === "confirm" ? "Confirm" : "OK");
  const finalCancelLabel = cancelText || cancelLabel || "Cancel";

  useEffect(() => {
    if (!modalOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !modalLocked && !busy && typeof onCancel === "function") {
        onCancel();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, modalLocked, busy, onCancel]);

  if (!modalOpen) return null;

  const isProgress = type === "progress";
  const isConfirm = type === "confirm";
  const isSuccess = type === "success";

  const getIcon = () => {
    switch (type) {
      case "success":
        return (
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              backgroundColor: "#ecfdf5",
              color: "#059669",
              display: "grid",
              placeItems: "center",
              fontSize: "22px",
              flexShrink: 0
            }}
          >
            ✓
          </div>
        );
      case "error":
        return (
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              backgroundColor: "#fef2f2",
              color: "#dc2626",
              display: "grid",
              placeItems: "center",
              fontSize: "20px",
              flexShrink: 0
            }}
          >
            ✕
          </div>
        );
      case "progress":
        return (
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              backgroundColor: "#eff6ff",
              color: "#2563eb",
              display: "grid",
              placeItems: "center",
              fontSize: "22px",
              flexShrink: 0
            }}
          >
            ⚡
          </div>
        );
      case "info":
        return (
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              backgroundColor: "#eff6ff",
              color: "#2563eb",
              display: "grid",
              placeItems: "center",
              fontSize: "20px",
              flexShrink: 0
            }}
          >
            ℹ️
          </div>
        );
      case "confirm":
      default:
        return (
          <div
            style={{
              width: "42px",
              height: "42px",
              borderRadius: "50%",
              backgroundColor: confirmTone === "danger" ? "#fef2f2" : "#fffbeb",
              color: confirmTone === "danger" ? "#dc2626" : "#d97706",
              display: "grid",
              placeItems: "center",
              fontSize: "20px",
              flexShrink: 0
            }}
          >
            {confirmTone === "danger" ? "⚠️" : "📝"}
          </div>
        );
    }
  };

  const normalizedPercent = Math.max(0, Math.min(100, Math.round(progress || 0)));

  return (
    <div
      className="feedback-modal"
      role="dialog"
      aria-modal="true"
      style={{
        zIndex: 9999,
        backgroundColor: "rgba(15, 23, 42, 0.65)",
        backdropFilter: "blur(4px)",
        padding: "16px"
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !modalLocked && !busy) {
          if (typeof onCancel === "function") onCancel();
          else if (typeof onConfirm === "function") onConfirm();
        }
      }}
    >
      <div
        className="feedback-card"
        style={{
          width: "min(480px, 100%)",
          background: "#ffffff",
          borderRadius: "16px",
          padding: "24px",
          boxShadow: "0 20px 35px -5px rgba(15, 23, 42, 0.25), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
          border: "1px solid #e2e8f0"
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", marginBottom: "14px" }}>
          {getIcon()}
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: "2px 0 4px 0", fontSize: "17px", fontWeight: "700", color: "#0f172a" }}>
              {title || (isConfirm ? "Confirm Action" : isProgress ? "Processing" : "Notice")}
            </h3>
            {message && (
              <div style={{ color: "#475569", fontSize: "14px", lineHeight: "1.55", wordBreak: "break-word" }}>
                {message}
              </div>
            )}
          </div>
          {!modalLocked && !busy && (typeof onCancel === "function" || typeof onConfirm === "function") && (
            <button
              type="button"
              onClick={onCancel || onConfirm}
              aria-label="Close dialog"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: "4px",
                color: "#94a3b8",
                fontSize: "18px",
                lineHeight: "1",
                borderRadius: "6px",
                transition: "color 0.15s ease"
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "#334155")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
            >
              ✕
            </button>
          )}
        </div>

        {/* Progress Bar & Details */}
        {isProgress && (
          <div style={{ margin: "14px 0 16px 0" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
              <span style={{ fontSize: "13px", fontWeight: "700", color: "#1e293b" }}>
                {progressLabel || "Processing..."}
              </span>
              <span style={{ fontSize: "14px", fontWeight: "800", color: "#2563eb", fontFamily: "monospace" }}>
                {normalizedPercent}%
              </span>
            </div>
            <div
              style={{
                width: "100%",
                height: "10px",
                backgroundColor: "#e2e8f0",
                borderRadius: "999px",
                overflow: "hidden",
                position: "relative"
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${normalizedPercent}%`,
                  backgroundColor: normalizedPercent === 100 ? "#10b981" : "#2563eb",
                  borderRadius: "999px",
                  transition: "width 0.3s ease-out, background-color 0.3s ease"
                }}
              />
            </div>
            {detail && (
              <div
                style={{
                  marginTop: "10px",
                  fontSize: "12px",
                  color: "#64748b",
                  backgroundColor: "#f8fafc",
                  padding: "8px 12px",
                  borderRadius: "8px",
                  border: "1px solid #f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}
              >
                <span className="live-ocr-spinner" style={{ width: "12px", height: "12px" }} />
                <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {detail}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "10px",
            marginTop: "18px",
            paddingTop: "14px",
            borderTop: "1px solid #f1f5f9"
          }}
        >
          {isConfirm && (
            <button
              type="button"
              className="secondary-button"
              onClick={onCancel}
              disabled={busy}
              style={{
                padding: "8px 16px",
                fontSize: "13px",
                fontWeight: "600",
                cursor: busy ? "not-allowed" : "pointer"
              }}
            >
              {finalCancelLabel}
            </button>
          )}

          {!isProgress || normalizedPercent === 100 ? (
            <button
              type="button"
              className={confirmTone === "danger" ? "danger-button" : "primary-button"}
              onClick={onConfirm || onCancel}
              disabled={busy}
              style={{
                padding: "8px 18px",
                fontSize: "13px",
                fontWeight: "700",
                backgroundColor:
                  confirmTone === "danger"
                    ? "#dc2626"
                    : confirmTone === "success" || isSuccess || normalizedPercent === 100
                    ? "#059669"
                    : "#2563eb",
                borderColor: "transparent",
                color: "#ffffff",
                cursor: busy ? "not-allowed" : "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px"
              }}
            >
              {busy ? "Working..." : normalizedPercent === 100 && isProgress ? "Inspect Graded Results" : finalConfirmLabel}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

